import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { useApps } from '../../contexts/AppsContext';
import { cn } from '../../utils/cn';

// Apps lists longer than this get a filter box at the top of the menu.
const SEARCH_THRESHOLD = 7;

const isDisabledApp = (app) => !(app?.is_active === 1 || app?.is_active === true || app?.is_active === '1');

const DisabledMarker = () => (
    <span className="shrink-0 rounded-full border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium leading-none text-slate-600">
        Inactive
    </span>
);

/**
 * The one app selector for app-scoped pages; lives in the shell's top bar. Reads/writes AppsContext.selectedAppId.
 * A single app renders as a static label (nothing to switch to).
 */
const AppSwitcher = ({ className }) => {
    const { apps, loading, error, selectedAppId, selectedApp, setSelectedAppId } = useApps();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);
    const rootRef = useRef(null);
    const buttonRef = useRef(null);
    const searchRef = useRef(null);
    const listRef = useRef(null);
    const baseId = useId();
    const labelId = `${baseId}-label`;
    const listId = `${baseId}-list`;
    const searchable = apps.length > SEARCH_THRESHOLD;

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return apps;
        return apps.filter(
            (a) => (a.app_name || '').toLowerCase().includes(q) || (a.app_id || '').toLowerCase().startsWith(q)
        );
    }, [apps, query]);

    // Close on outside click.
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    // On open: start on the current app and move focus into the menu.
    useEffect(() => {
        if (!open) return;
        const idx = Math.max(
            0,
            apps.findIndex((a) => a.app_id === selectedAppId)
        );
        setActive(idx);
        setQuery('');
        requestAnimationFrame(() => (searchable ? searchRef.current : listRef.current)?.focus());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Keep the active option in view.
    useEffect(() => {
        if (!open) return;
        listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
    }, [active, open]);

    if (loading && !apps.length) {
        return (
            <div className={cn('flex items-center gap-2', className)} aria-busy="true">
                <div className="sr-only text-sm font-medium text-slate-600 sm:not-sr-only">App</div>
                <div className="h-9 w-40 rounded-lg bg-slate-100 animate-pulse sm:w-56" />
            </div>
        );
    }

    if (error && !apps.length) {
        return (
            <div className={cn('text-sm text-red-700', className)} role="alert">
                Couldn’t load apps. Reload the page to try again.
            </div>
        );
    }

    if (!apps.length) {
        return (
            <div className={cn('text-sm text-slate-600', className)}>No apps yet. Register one on the Apps page.</div>
        );
    }

    if (apps.length === 1) {
        const only = apps[0];
        return (
            <div className={cn('flex items-center gap-2', className)}>
                <div className="sr-only text-sm font-medium text-slate-600 sm:not-sr-only">App</div>
                <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-slate-900">
                    <span className="truncate" title={only.app_name}>
                        {only.app_name}
                    </span>
                    {isDisabledApp(only) && <DisabledMarker />}
                </div>
            </div>
        );
    }

    const choose = (app) => {
        if (!app) return;
        if (app.app_id !== selectedAppId) setSelectedAppId(app.app_id);
        setOpen(false);
        buttonRef.current?.focus();
    };

    const onMenuKeyDown = (e) => {
        const last = filtered.length - 1;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActive((i) => Math.min(last, i + 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActive((i) => Math.max(0, i - 1));
                break;
            case 'Home':
                if (!searchable) {
                    e.preventDefault();
                    setActive(0);
                }
                break;
            case 'End':
                if (!searchable) {
                    e.preventDefault();
                    setActive(last);
                }
                break;
            case 'Enter':
                e.preventDefault();
                choose(filtered[active]);
                break;
            case ' ':
                if (!searchable) {
                    e.preventDefault();
                    choose(filtered[active]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
                buttonRef.current?.focus();
                break;
            case 'Tab':
                setOpen(false);
                break;
            default:
        }
    };

    const activeId = filtered[active] ? `${baseId}-opt-${active}` : undefined;

    return (
        <div ref={rootRef} className={cn('relative flex min-w-0 items-center gap-2', className)}>
            <div id={labelId} className="sr-only text-sm font-medium text-slate-600 sm:not-sr-only">
                App
            </div>
            <button
                ref={buttonRef}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={open ? listId : undefined}
                aria-labelledby={`${labelId} ${baseId}-value`}
                onClick={() => setOpen((o) => !o)}
                onKeyDown={(e) => {
                    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                        e.preventDefault();
                        setOpen(true);
                    }
                }}
                className="flex h-11 w-full min-w-0 items-center sm:h-9 sm:w-56 gap-2 rounded-control border border-line bg-surface px-2.5 text-left text-sm text-slate-900 shadow-sm transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:border-primary-500"
            >
                <span id={`${baseId}-value`} className="min-w-0 flex-1 truncate font-medium">
                    {selectedApp?.app_name || 'Select an app'}
                </span>
                {selectedApp && isDisabledApp(selectedApp) && <DisabledMarker />}
                <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
            </button>

            {open && (
                <div className="absolute right-0 top-full z-50 mt-1 w-64 max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
                    {searchable && (
                        <div className="relative border-b border-slate-200">
                            <Search
                                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
                                aria-hidden="true"
                            />
                            <input
                                ref={searchRef}
                                type="text"
                                role="combobox"
                                aria-label="Filter apps"
                                aria-controls={listId}
                                aria-expanded="true"
                                aria-autocomplete="list"
                                aria-activedescendant={activeId}
                                value={query}
                                onChange={(e) => {
                                    setQuery(e.target.value);
                                    setActive(0);
                                }}
                                onKeyDown={onMenuKeyDown}
                                placeholder="Filter apps…"
                                className="w-full bg-transparent py-2 pl-8 pr-2 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none"
                            />
                        </div>
                    )}
                    <ul
                        ref={listRef}
                        id={listId}
                        role="listbox"
                        aria-labelledby={labelId}
                        tabIndex={-1}
                        aria-activedescendant={searchable ? undefined : activeId}
                        onKeyDown={searchable ? undefined : onMenuKeyDown}
                        className="max-h-72 overflow-y-auto py-1 focus:outline-none"
                    >
                        {filtered.length === 0 && (
                            <li className="px-3 py-2 text-sm text-slate-600">No apps match “{query.trim()}”.</li>
                        )}
                        {filtered.map((app, i) => {
                            const selected = app.app_id === selectedAppId;
                            return (
                                <li
                                    key={app.app_id}
                                    id={`${baseId}-opt-${i}`}
                                    data-index={i}
                                    role="option"
                                    aria-selected={selected}
                                    onMouseEnter={() => setActive(i)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => choose(app)}
                                    className={cn(
                                        'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-slate-800',
                                        i === active && 'bg-slate-100'
                                    )}
                                >
                                    <Check
                                        className={cn('h-4 w-4 shrink-0 text-primary-600', !selected && 'invisible')}
                                        aria-hidden="true"
                                    />
                                    <span className="min-w-0 flex-1 truncate" title={app.app_name}>
                                        {app.app_name}
                                    </span>
                                    {isDisabledApp(app) && <DisabledMarker />}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default AppSwitcher;
